import { Typography } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { distanceOfTimeInWords } from '../../../../../../Time';
import { L, T, TPlainTextKey } from '../../../../../../Translations';
import { participantSearchStore } from '../Stores/ParticipantSearchStore';

const COLUMNS: ReadonlyArray<TPlainTextKey> = [
  'Full Name',
  'Email',
  'Licenses',
  'Collections',
  'Last Login',
];

const PeopleTable: React.FC = () => {
  const users = participantSearchStore.users;
  const selectedUserIds = participantSearchStore.selectedUserIds;
  const pinnedUserIds = participantSearchStore.pinnedUserIds;

  const licensedUsers = users.filter((user) => user.licenseCount > 0);
  const allSelected =
    licensedUsers.length > 0 && licensedUsers.every((user) => selectedUserIds.has(user.id));
  const someSelected = licensedUsers.some((user) => selectedUserIds.has(user.id)) && !allSelected;

  return (
    <div>
      {/* Results count */}
      <Typography variant="bodyMedium" className="text-muted-foreground mb-4">
        <T
          kind="Viewing {{resultsCount}} of {{totalCount}} participants"
          resultsCount={users.length}
          totalCount={users.length}
        />
        {selectedUserIds.size > 0 && (
          <>
            {' '}
            <T kind="({{count}} selected)" count={selectedUserIds.size} />
          </>
        )}
      </Typography>

      {/* Results table */}
      <div
        className="border border-border rounded-lg overflow-hidden"
        data-intercom-target="participant-search-people-table"
      >
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded cursor-pointer text-primary border-border focus:ring-primary"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={() => participantSearchStore.toggleAllUsers()}
                />
              </th>
              {COLUMNS.map((column) => (
                <th key={column} className="px-4 py-3 text-left">
                  <Typography
                    variant="caption"
                    fontWeight="semibold"
                    className="text-muted-foreground"
                  >
                    <T kind={column} />
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center">
                  <Typography variant="bodyMedium" as="span" className="text-muted-foreground">
                    <T kind="No participants found" />
                  </Typography>
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const isPinned = pinnedUserIds.has(user.id);
                const hasLicense = user.licenseCount > 0;
                return (
                  <tr
                    key={user.id}
                    className={clsx({
                      'bg-primary/10': isPinned,
                      'bg-muted': !hasLicense,
                    })}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded cursor-pointer text-primary border-border focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                        checked={selectedUserIds.has(user.id)}
                        disabled={!hasLicense}
                        onChange={() => participantSearchStore.toggleUserSelection(user.id)}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Typography variant="bodyMedium" as="span" className="text-primary">
                        {user.firstName} {user.lastName}
                      </Typography>
                    </td>
                    <td className="px-4 py-4">
                      <Typography variant="bodyMedium" as="span">
                        {user.email}
                      </Typography>
                    </td>
                    <td className="px-4 py-4">
                      <Typography variant="bodyMedium" as="span">
                        {user.licenseCount === 0 ? <T kind="Unlicensed" /> : user.licenseCount}
                      </Typography>
                    </td>
                    <td className="px-4 py-4">
                      <Typography variant="bodyMedium" as="span">
                        {user.learningCollectionCount}
                      </Typography>
                    </td>
                    <td className="px-4 py-4">
                      <Typography variant="bodyMedium" as="span">
                        {user.lastLogin.cata({
                          Just: (date: Date) => (
                            <>
                              <span className="mr-1">
                                <L localizeable={date} format="short-month-day-year" />
                              </span>
                              <span className="mr-1">
                                <L localizeable={date} format="time-of-day" />
                              </span>
                              <span>
                                {'('}
                                <T {...distanceOfTimeInWords(new Date(), date)} />
                                {')'}
                              </span>
                            </>
                          ),
                          Nothing: () => <T kind="Never" />,
                        })}
                      </Typography>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default observer(PeopleTable);
